import { useEffect, useState } from 'react';
import { getVoiceUsage, type VoiceUsageRow } from '../services/voiceUsageStatsService';

const DAY_MS = 24 * 60 * 60 * 1000;

/** YYYY-MM-DD in UTC, the day format of voice_usage_daily. */
const toDay = (date: Date): string => date.toISOString().slice(0, 10);

const formatMinutes = (secs: number): string => `${Math.round(secs / 60).toLocaleString()} min`;

const formatPercent = (value: number, total: number): string =>
    total > 0 ? `${Math.round((value / total) * 100)}%` : '–';

const inputClass = 'px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

/** Which voice mode voices users use, and how often they change away from each one. */
export const VoiceUsageStats = () => {
    const [from, setFrom] = useState(() => toDay(new Date(Date.now() - 29 * DAY_MS)));
    const [to, setTo] = useState(() => toDay(new Date()));
    const [rows, setRows] = useState<VoiceUsageRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isCurrent = true;
        setRows(null);
        setError(null);
        getVoiceUsage(from, to)
            .then((result) => { if (isCurrent) setRows(result); })
            .catch((err: unknown) => {
                console.error('Failed to load voice usage:', err);
                if (isCurrent) setError('Failed to load voice usage');
            });
        return () => { isCurrent = false; };
    }, [from, to]);

    const totalSecs = rows?.reduce((sum, row) => sum + row.secs, 0) ?? 0;

    return (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 space-y-4">
            <div className="flex justify-between items-start gap-4 flex-wrap">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Voice Usage</h2>
                    <p className="text-sm text-gray-500 max-w-2xl">
                        Minutes each voice spoke in voice mode calls, by UTC day. A call that changed voice counts for each voice it used.
                        "Changed away" counts the times users changed from the voice to another during a call: a high rate suggests users do not like the voice.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    <input type="date" className={inputClass} value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
                    <span className="text-gray-500">to</span>
                    <input type="date" className={inputClass} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
            )}

            {!rows && !error && <div className="text-gray-600">Loading voice usage...</div>}

            {rows && rows.length === 0 && (
                <div className="text-center py-8 text-gray-500">No voice mode calls in this date range.</div>
            )}

            {rows && rows.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500 border-b border-gray-200">
                                <th className="py-2 pr-4 font-medium">Voice</th>
                                <th className="py-2 pr-4 font-medium text-right">Minutes</th>
                                <th className="py-2 pr-4 font-medium text-right">Share</th>
                                <th className="py-2 pr-4 font-medium text-right">Calls</th>
                                <th className="py-2 pr-4 font-medium text-right">Changed away</th>
                                <th className="py-2 font-medium text-right">Avg time before change</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.voiceId} className="border-b border-gray-100">
                                    <td className="py-2 pr-4">
                                        <span className="text-gray-900">{row.name ?? 'Deleted voice'}</span>
                                        <span className="text-xs text-gray-500 ml-2">({row.voiceId})</span>
                                    </td>
                                    <td className="py-2 pr-4 text-right">{formatMinutes(row.secs)}</td>
                                    <td className="py-2 pr-4 text-right">{formatPercent(row.secs, totalSecs)}</td>
                                    <td className="py-2 pr-4 text-right">{row.calls.toLocaleString()}</td>
                                    <td className="py-2 pr-4 text-right">
                                        {row.changedAway.toLocaleString()}
                                        <span className="text-xs text-gray-500 ml-1">({formatPercent(row.changedAway, row.calls)} of calls)</span>
                                    </td>
                                    <td className="py-2 text-right">
                                        {row.changedAway > 0 ? `${Math.round(row.changedAwaySecs / row.changedAway)}s` : '–'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
